import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatMessage } from "lib/globalize-util";
import SearchIcon from "@material-ui/icons/Search";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {
    TableContainer,
    CircularProgress,
    Checkbox,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@material-ui/core";
import theme from "styles/theme";
import { ProductListRow } from "./product-list-row";
import {
    IProductDataEntry,
    TProductIds,
    IBulkCustomizationQuery,
    TBulkCustomizationQueryFilter,
} from "constants/product-customizations/customization";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import { connect, Dispatch } from "react-redux";
import { fetchBulkCustomizationProducts, resetBulkCustomizationProducts } from "actions/customization-actions";
import {
    CustomizationSelectionType,
    TCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import { IProduct } from "records/item/item";
import { useDebounce, useSkipFirstEffect } from "hooks";
import { IStore } from "reducers";
import { BrandRecord } from "records/brand";

interface IProductListProps {
    isPDP: boolean;
    cartProductsData?: IProductDataEntry[];
    onSelectedProductsChange: (selectedProductData: IProductDataEntry[]) => void;
    customizations: TCustomizationSelection[];
    mainProductId: string;
}

interface IProductListStateProps {
    customizationTemplateIdFromWO: string;
    customizationTemplateIdFromPDP: string;
    bulkCustomizationProducts: IProduct[];
    isFetchingProducts: boolean;
    brand: BrandRecord | null;
}

interface IProductListDispatchProps {
    fetchBulkProducts: (params: IBulkCustomizationQuery) => void;
    onResetBulkCustomizationProducts: () => void;
}

interface IHeadCell {
    id: "image" | "season" | "name" | "style_number" | "color";
    label: string;
    width: string;
}

const PRODUCTS_FETCH_LIMIT = 10;
const SCROLL_BOTTOM_DISTANCE = 100;

export const ProductListContainer: React.FC<IProductListProps & IProductListStateProps & IProductListDispatchProps> = ({
    isPDP,
    cartProductsData,
    onSelectedProductsChange,
    customizationTemplateIdFromWO,
    customizationTemplateIdFromPDP,
    customizations,
    bulkCustomizationProducts,
    fetchBulkProducts,
    isFetchingProducts,
    onResetBulkCustomizationProducts,
    mainProductId,
    brand,
}) => {
    const classes = useStyles();
    const [selected, setSelected] = React.useState<TProductIds>([]);
    const [productsToShow, setProductsToShow] = useState<IProductDataEntry[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const tableEl = useRef<HTMLDivElement>(null);
    const hasMore = bulkCustomizationProducts.length > 0;

    const fetchProducts = (search: string = "", useLastId: boolean = false) => {
        const lastId = useLastId ? bulkCustomizationProducts[bulkCustomizationProducts.length - 1]?._id : undefined;

        const imageIds: string[] = customizations.reduce((ids, currentCustomization) => {
            if (currentCustomization.type === CustomizationSelectionType.PRINTED_IMAGE) {
                ids.push(currentCustomization.value);
            }
            return ids;
        }, [] as string[]);

        const filters: TBulkCustomizationQueryFilter = isPDP
            ? {}
            : {
                  _id: cartProductsData?.map(({ product }) => product._id) ?? [],
              };

        const templateId = isPDP ? customizationTemplateIdFromPDP : customizationTemplateIdFromWO;

        fetchBulkProducts({
            isPDP,
            templateId,
            filters,
            imageIds,
            lastId,
            limit: PRODUCTS_FETCH_LIMIT,
            search,
            sort: {
                sort: "_id",
                sortOrder: "desc",
            },
        });
    };

    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (bulkCustomizationProducts.length === 0) {
            return;
        }

        let newProductsData: IProductDataEntry[] = [];

        if (isPDP) {
            newProductsData = bulkCustomizationProducts
                .filter((product: IProduct) => product._id !== mainProductId)
                .map((product: IProduct) => ({ product }));
        } else {
            const fetchedProductIds = new Set(bulkCustomizationProducts.map((product) => product._id));
            newProductsData = cartProductsData?.filter(({ product }) => fetchedProductIds.has(product._id)) ?? [];
        }

        setProductsToShow((productsToShow) => [...productsToShow, ...newProductsData]);
    }, [isPDP, cartProductsData, bulkCustomizationProducts, mainProductId]);

    useSkipFirstEffect(() => {
        onResetBulkCustomizationProducts();
        setProductsToShow([]);
        fetchProducts(debouncedSearchValue, false);
    }, [debouncedSearchValue]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const scrollListener = () => {
        const { scrollHeight, clientHeight, scrollTop } = tableEl?.current ?? ({} as HTMLDivElement);

        if (scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_DISTANCE && !isFetchingProducts && hasMore) {
            fetchProducts(searchValue, true);
        }
    };

    useLayoutEffect(() => {
        const tableRef = tableEl.current;
        tableRef?.addEventListener("scroll", scrollListener);
        return () => {
            tableRef?.removeEventListener("scroll", scrollListener);
        };
    }, [scrollListener]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchValue(value);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const selectedProductIds = checked
            ? productsToShow.map(({ lineItem, product }) => lineItem?._id ?? product._id)
            : [];
        const productsToSelect = checked ? productsToShow : [];

        onSelectedProductsChange(productsToSelect);
        setSelected(selectedProductIds);
    };

    const handleClick = (event: React.MouseEvent<unknown>, productDataEntry: IProductDataEntry) => {
        const itemId = productDataEntry.lineItem?._id ?? productDataEntry.product._id;
        const selectedIndex = selected.indexOf(itemId);
        const newSelected: string[] = [...selected];

        if (selectedIndex === -1) {
            newSelected.push(itemId);
        } else {
            newSelected.splice(selectedIndex, 1);
        }

        onSelectedProductsChange(
            productsToShow.filter(({ lineItem, product }) => {
                const itemId = lineItem?._id ?? product._id;
                return newSelected.includes(itemId);
            }),
        );
        setSelected(newSelected);
    };

    const headCells: IHeadCell[] = [
        { id: "image", label: formatMessage("Image"), width: "8%" },
        { id: "name", label: formatMessage("Name"), width: "22%" },
        { id: "style_number", label: formatMessage("Style Number"), width: "22%" },
        { id: "season", label: formatMessage("Season"), width: "22%" },
        { id: "color", label: formatMessage("Color"), width: "22%" },
    ];

    return (
        <div>
            <div className={classes.productSearch}>
                <div className={classes.searchTitle}>{formatMessage("Search Products")}: </div>
                <FormControl fullWidth className={classes.searchImage} variant="outlined">
                    <OutlinedInput
                        data-test={CUSTOMIZATIONS.BULK_CUSTOMIZATION_PRODUCT_SEARCH}
                        value={searchValue}
                        onChange={handleSearchChange}
                        placeholder={formatMessage("Search")}
                        startAdornment={
                            <InputAdornment position="start">
                                <SearchIcon className={classes.searchIcon} />
                            </InputAdornment>
                        }
                    />
                </FormControl>
            </div>
            <div>
                <TableContainer style={styles.tableContainer} ref={tableEl}>
                    <Table data-test={CUSTOMIZATIONS.PRODUCT_LIST_TABLE} stickyHeader={true}>
                        <TableHead>
                            <TableRow data-test={CUSTOMIZATIONS.PRODUCT_LIST_ROW}>
                                <TableCell padding="checkbox" style={{ width: "4%" }}>
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < productsToShow.length}
                                        checked={productsToShow.length > 0 && selected.length === productsToShow.length}
                                        onChange={handleSelectAllClick}
                                        style={{ color: theme.palette.primary1Color }}
                                        inputProps={{ "aria-label": "select all products" }}
                                    />
                                </TableCell>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        className={classes.tableHeader}
                                        key={headCell.id}
                                        style={{ width: headCell.width }}
                                        data-test={CUSTOMIZATIONS.PRODUCT_LIST_ROW_HEADER_CELL}
                                    >
                                        {headCell.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {productsToShow.map((productData, index) => (
                                <ProductListRow
                                    brand={brand}
                                    key={index}
                                    productDataEntry={productData}
                                    index={index}
                                    selected={selected}
                                    onRowClick={handleClick}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {isFetchingProducts && productsToShow.length === 0 && (
                    <div style={styles.progressContainer}>
                        <CircularProgress size={46} color="inherit" />
                    </div>
                )}
                {!isFetchingProducts && bulkCustomizationProducts.length === 0 && productsToShow.length === 0 && (
                    <div style={styles.noItemsLayout}>{formatMessage("No products found")}</div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state: IStore): IProductListStateProps => {
    const { customization, scope } = state;
    const { bulkCustomization: bulk, current } = customization;

    return {
        brand: scope.brand,
        bulkCustomizationProducts: bulk?.payload || [],
        isFetchingProducts: bulk?.isFetchingProducts,
        customizationTemplateIdFromWO: bulk?.templateId,
        customizationTemplateIdFromPDP: current?.payload?._id,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<{}>): IProductListDispatchProps => ({
    fetchBulkProducts: (params: IBulkCustomizationQuery) => dispatch(fetchBulkCustomizationProducts(params)),
    onResetBulkCustomizationProducts: () => dispatch(resetBulkCustomizationProducts()),
});

export const ProductList = connect<IProductListStateProps, IProductListDispatchProps, IProductListProps>(
    mapStateToProps,
    mapDispatchToProps,
)(ProductListContainer);

const useStyles = makeStyles({
    searchImage: {
        width: "100%",
        "& .MuiInputBase-input": {
            border: "none",
            fontSize: "16px",
            padding: "8px 0",
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.blue,
                borderWidth: "1px",
            },
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.borderColor,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.borderColor,
        },
    },
    searchTitle: {
        color: theme.palette.darkestGrey,
        marginBottom: "10px",
        fontWeight: "bold",
    },
    searchIcon: {
        fontSize: "20px",
        color: theme.palette.grey,
    },
    productSearch: {
        marginBottom: "20px",
        fontSize: "14px",
    },
    tableHeader: {
        fontSize: "14px",
    },
});

const styles = {
    progressContainer: {
        width: "100%",
        height: "300px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: theme.palette.primary1Color,
    },
    noItemsLayout: {
        width: "100%",
        height: "300px",
        display: "flex",
        fontSize: "15px",
        alignItems: "center",
        justifyContent: "center",
    },
    progress: {
        margin: "60px auto",
        display: "block",
    },
    tableContainer: {
        maxWidth: "100%",
        margin: "auto",
        maxHeight: "350px",
        overscrollBehavior: "contain",
    },
};
